// pdfPhotos.js
// Shared photo handling for every jsPDF report generator.
//
// This exists because the same three jobs — load a photo, draw it without
// distorting it, and reserve the right amount of page for it — were previously
// copy-pasted into six generators with six subtly different loaders and no
// shared geometry. A sizing change used to mean six edits.

// Photo band geometry, in mm. Cells are SQUARE on purpose.
//
// Aspect ratio is preserved when drawing (see drawPhoto), and fitting a
// portrait phone photo into a landscape cell wastes most of the cell: a 3:4
// shot in a 90x68 box comes out ~51mm wide, barely bigger than the 55mm boxes
// this replaced. A square cell gives portrait (66x88) and landscape (88x66)
// the same area, so the band reads evenly whatever the tech happened to shoot.
//
// If the field says these are too big or too small, this is the one place to
// change it.
export const PHOTO = {
  perRow: 2,
  cell: 88, // inspection report; narrower-margin reports pass their own
  gap: 6, // between cells
  pad: 4, // band inset from the content edge
  captionH: 5, // room under each cell for "Photo 1"
};

// Fetch a file and hand back a data URL, preserving the original bytes.
// Photos are the main use, but the general quote loads its attachments the same
// way — anything jsPDF might embed.
//
// Deliberately NOT canvas-based. Re-encoding through
// canvas.toDataURL("image/png") — which four of these generators used to do —
// turns an already-compressed 2000px JPEG into a multi-megabyte PNG. The
// photos arrive as JPEG from prepareImageForUpload; keep them that way.
//
// Returns null rather than throwing: a missing photo must never take the whole
// report down with it.
export async function loadPhotoDataURL(url) {
  if (!url) return null;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("Could not load photo:", url, e);
    return null;
  }
}

// jsPDF wants an explicit format. Read it off the data URL rather than
// guessing — a hard-coded "PNG" was only ever correct because of the canvas
// re-encode above.
function formatOf(dataUrl) {
  const m = /^data:image\/([a-z0-9+]+)/i.exec(dataUrl || "");
  if (!m) return "JPEG";
  const fmt = m[1].toUpperCase();
  return fmt === "JPG" ? "JPEG" : fmt;
}

// Cell geometry for a band `width` wide. Each generator has its own margin, so
// the cell is derived from the width it actually has rather than assumed.
export function photoCellSize(width, opts = {}) {
  const { perRow = PHOTO.perRow, gap = PHOTO.gap, pad = PHOTO.pad } = opts;
  const usable = width - pad * 2 - gap * (perRow - 1);
  const cellW = usable / perRow;
  // Square, capped so a wide band doesn't produce absurdly tall rows.
  const cellH = Math.min(cellW, opts.maxCellH ?? cellW);
  return { cellW, cellH, perRow, gap, pad };
}

// Height a band of `count` photos will consume. Callers use this for their
// page-break reservation, so what they reserve is exactly what gets drawn —
// the old code reserved 36 and then consumed 38.
export function photoBandHeight(count, width, opts = {}) {
  if (!count) return 0;
  const { cellH, perRow, pad } = photoCellSize(width, opts);
  const captionH = opts.captionH ?? PHOTO.captionH;
  const rows = Math.ceil(count / perRow);
  return rows * (pad + cellH + captionH);
}

// Draw one photo fitted inside a box, aspect ratio preserved and centred.
//
// Returns the rect actually drawn, or null if the image could not be drawn at
// all. The border goes around the IMAGE, not the box, so a portrait photo in a
// square cell has no grey bars beside it.
//
// `opts.valign` defaults to "middle", which is what a grid cell wants. Pass
// "top" for a standalone photo whose box is a maximum rather than a slot: the
// caller can then advance its cursor by the returned height alone, with no
// leftover centring offset to account for.
// Size a photo will occupy inside a box, without drawing it. Callers that
// reserve page space for a single photo use this so they reserve what the photo
// actually needs — reserving the full box would push a landscape shot onto a
// new page while a page and a half of room went unused.
export function photoFitSize(doc, dataUrl, boxW, boxH) {
  try {
    const props = doc.getImageProperties(dataUrl);
    if (props?.width > 0 && props?.height > 0) {
      const scale = Math.min(boxW / props.width, boxH / props.height);
      return { w: props.width * scale, h: props.height * scale };
    }
  } catch (e) {
    // Dimensions unavailable — fall back to filling the box. Distorted beats
    // absent.
  }
  return { w: boxW, h: boxH };
}

export function drawPhoto(doc, dataUrl, boxX, boxY, boxW, boxH, opts = {}) {
  if (!dataUrl) return null;
  const fmt = formatOf(dataUrl);
  const { w, h } = photoFitSize(doc, dataUrl, boxW, boxH);

  const x = boxX + (boxW - w) / 2;
  const y = opts.valign === "top" ? boxY : boxY + (boxH - h) / 2;

  try {
    doc.addImage(dataUrl, fmt, x, y, w, h, undefined, "FAST");
  } catch (e) {
    return null;
  }

  if (opts.border !== false) {
    doc.setDrawColor(...(opts.borderColor || [180, 180, 180]));
    doc.setLineWidth(0.3);
    doc.rect(x, y, w, h);
  }
  return { x, y, w, h };
}

// Draw a band of photos below an item's text row.
//
// Page-breaks and paints its background PER ROW rather than painting one rect
// for the whole band up front: with cells this tall a band can straddle a page
// boundary, and the old approach left a half-painted band behind.
//
// `photos` are data URLs (already loaded). `checkPageBreak(needed)` is passed
// in because the generators name theirs differently. `getY`/`setY` let the
// caller keep owning its own `y` cursor across the page breaks.
export function drawPhotoBand(doc, photos, opts) {
  const {
    x,
    width,
    getY,
    setY,
    checkPageBreak,
    bandColor = [255, 245, 245],
    captionPrefix = "Photo",
    captions = true,
    // One caption per photo, in the same order. Falls back to "Photo N" for
    // any entry left blank — used where the caption carries real information,
    // such as when a photo was received.
    captionTexts = null,
    textColor = [120, 120, 120],
    separator = true,
  } = opts;

  const list = (photos || []).filter(Boolean);
  if (list.length === 0) return;

  const { cellW, cellH, perRow, gap, pad } = photoCellSize(width, opts);
  const captionH = opts.captionH ?? PHOTO.captionH;
  const rowH = pad + cellH + captionH;

  for (let start = 0; start < list.length; start += perRow) {
    const row = list.slice(start, start + perRow);
    checkPageBreak(rowH);
    let y = getY();

    doc.setFillColor(...bandColor);
    doc.rect(x, y, width, rowH, "F");

    row.forEach((dataUrl, i) => {
      const cellX = x + pad + i * (cellW + gap);
      const cellY = y + pad;
      const drawn = drawPhoto(doc, dataUrl, cellX, cellY, cellW, cellH);

      if (!drawn) {
        // Placeholder so a failed image still reads as "a photo belongs here"
        // rather than as empty space.
        doc.setFillColor(240, 240, 240);
        doc.rect(cellX, cellY, cellW, cellH, "F");
      }
      if (captions) {
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textColor);
        doc.text(
          (captionTexts && captionTexts[start + i]) ||
            `${captionPrefix} ${start + i + 1}`,
          cellX + cellW / 2,
          cellY + cellH + captionH - 1,
          { align: "center" },
        );
      }
    });

    y += rowH;
    setY(y);
  }

  if (separator) {
    const y = getY();
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.line(x, y, x + width, y);
  }
}
